/** @jsxImportSource @emotion/react */
import * as yup from "yup";
import * as React from "react";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import * as Inputs from "../common/hook-form/index";
import { routes } from "../../appRoutingHelpers";
import { SpinnerBlock } from "../common/Spinner";
import useUpdateFormErrors from "../common/hook-form/useUpdateFormErrors";
import { StyledText } from "../../assets/typography/typography";
import { colors } from "../../components/colors";
import { PrimaryButton } from "../../components/Buttons";
import { FormRow } from "../common/hook-form/index";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { GoBackLinkButton } from "../../components/GoBackLinkButton";
import {
  useArchiveAdditionGroup,
  useCreateAdditionGroup,
  useGetAdditionGroup,
  useUpdateAdditionGroup,
} from "../../service/api";
import {
  AdditionGroupData,
  AdditionGroupOptionsData,
} from "./model/ShopProductAdditionsListSuccessResponse";
import { css } from "@emotion/react";
import { ProductAddition } from "./ProductAddition";
import { toastr } from "react-redux-toastr";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  PriceInput,
  transformPriceInput,
  validateAboveOrEqualToZero,
} from "../../components/forms/inputs/PriceInput";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";
import { transformPriceFromAPI, useIsUsingSharedPrice } from "../../util";
import { TextInput } from "../../components/forms/inputs/TextInput";
import { SelectProductsSelector } from "../../components/SelectProductsSelector";
import { ButtonAction } from "../../components/NewButtons";
import { ActionSpinner } from "../../components/ActionSpinner";
import { useSearchParamsManipulation } from "../../hooks/use-searchparams-manipulation";

const typeOptions = [
  { label: "Single", value: "single" },
  { label: "Multi", value: "multi" },
];

interface Props {}

type FormProduct = {
  id: string;
  name: string;
  title: string;
  isPreselected: boolean;
  minimumSelections?: number | null;
  maximumSelections?: number | null;
  additionalSelectionPriceAmount?: number | string | null;
  additionalSelectionPriceCurrency?: string | null;
};

export interface FormValues {
  name: string;
  title: string;
  type: string;
  sortKey: number;
  sharedOptionSelectionLimit?: number | null;
  sharedOptionSelectionPriceValue?: string | number | null;
  products: FormProduct[];
  minimumSelections: number;
  maximumSelections: number | null;
}

const additionsWrapper = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;
  margin-bottom: 16px;
`;

const ProductAdditionsUpsert: React.FC<Props> = (props) => {
  const [currencyOfFirstProduct, setCurrencyOfFirstProduct] = React.useState<
    string | undefined
  >();
  const { classification } = useSearchParamsManipulation();
  const formMethods = useForm<FormValues>({
    defaultValues: {
      products: [],
      maximumSelections: 1,
      minimumSelections: 0,
      sharedOptionSelectionPriceValue: "",
      sharedOptionSelectionLimit: null,
    },
    reValidateMode: "onChange",
    resolver: yupResolver(additionGroupSchema),
  });
  const navigate = useNavigate();
  // --- TEMP FOR SHARED OPTION SELECTION LIMIT --- //
  const isUsingSharedPrice = useIsUsingSharedPrice();
  // ---------------------------------------------- //
  const { handleSubmit, setError, control, reset } = formMethods;
  const { fields, remove, replace } = useFieldArray({
    control,
    name: "products",
  });

  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;

  const organization =
    useSelector(authSelectors.selectedOrganizationKeyName) || "";

  const { data, isLoading: fetchIsLoading, error } = useGetAdditionGroup(
    organization,
    id
  );

  const {
    mutateAsync: deleteAdditionGroup,
    isLoading: deleteIsLoading,
  } = useArchiveAdditionGroup();

  const {
    mutateAsync: createAdditionGroup,
    isLoading: creationisLoading,
  } = useCreateAdditionGroup();

  const {
    mutateAsync: updateAdditionGroup,
    isLoading: updateisLoading,
  } = useUpdateAdditionGroup(id);

  const [
    isMaximumSelectionsVisible,
    setMaximumSelectionsVisible,
  ] = React.useState(false);

  useUpdateFormErrors(error, setError);

  React.useEffect(
    function resetFormWhenEditng() {
      if (isEdit && data) {
        const { configs, ...rest } = data;
        const formValues: FormValues = {
          maximumSelections: rest.maximumSelections,
          minimumSelections: rest.minimumSelections,
          name: rest.name,
          products: [],
          sortKey: rest.sortKey,
          title: rest.title,
          type: rest.type,
          sharedOptionSelectionLimit: rest.sharedOptionSelectionLimit,
        };

        formValues.products = configs.map((c, i) => {
          if (i === 0 && rest.sharedOptionSelectionPriceValue) {
            const currencyToUse = c.additionalSelectionPrice.currency;
            setCurrencyOfFirstProduct(currencyToUse);
            formValues.sharedOptionSelectionPriceValue = transformPriceFromAPI(
              rest.sharedOptionSelectionPriceValue,
              currencyToUse
            );
          }
          const product: FormProduct = {
            ...c.product,
            isPreselected: c.selected,
            maximumSelections: c.maximumSelections || 1,
            minimumSelections: c.minimumSelections || 0,
          };
          if (c?.additionalSelectionPrice.currency) {
            product.additionalSelectionPriceCurrency =
              c?.additionalSelectionPrice.currency;
          }
          if (
            c?.additionalSelectionPrice.amount ||
            c?.additionalSelectionPrice.amount === 0
          ) {
            product.additionalSelectionPriceAmount = transformPriceFromAPI(
              c?.additionalSelectionPrice.amount,
              c?.additionalSelectionPrice.currency
            );
          }
          return product;
        });

        reset(formValues);
        if (data.type !== "single") {
          setMaximumSelectionsVisible(true);
        }
      }
    },
    [isEdit, data, reset]
  );

  const handleDelete = async (id: string) => {
    try {
      await deleteAdditionGroup({
        id,
        organizationKeyName: organization,
      });
      toastr.success("Success", "You succesfully deleted the addition group");
      navigate(routes.shopProductAdditionsList(organization));
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const submitHandler = async (data: AdditionGroupData) => {
    try {
      if (isEdit) {
        await updateAdditionGroup({
          organizationKeyName: organization,
          data,
          id: id!,
        });
        toastr.success("Success", "You succesfully updated the addition group");
      } else {
        await createAdditionGroup({
          organizationKeyName: organization,
          data,
        });
        toastr.success("Success", "You succesfully created the addition group");
      }
      navigate(routes.shopProductAdditionsList(organization));
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const onSubmit = async (data: FormValues) => {
    const { products, sharedOptionSelectionPriceValue, ...rest } = data;
    const toSubmit: AdditionGroupData = { ...rest, options: [], products: [] };

    if (!toSubmit.minimumSelections) {
      toSubmit.minimumSelections = 0;
    }

    if (!rest.sharedOptionSelectionLimit) {
      toSubmit.sharedOptionSelectionLimit = null;
    }

    if (currencyOfFirstProduct && sharedOptionSelectionPriceValue) {
      toSubmit.sharedOptionSelectionPriceValue = transformPriceInput(
        sharedOptionSelectionPriceValue,
        currencyOfFirstProduct
      );
    } else {
      toSubmit.sharedOptionSelectionPriceValue = null;
    }

    if (!toSubmit.maximumSelections) {
      toSubmit.maximumSelections = null;
    }

    toSubmit.products = products?.map((x) => x.id) || [];
    toSubmit.options = products?.map((x: FormProduct) => {
      const option: AdditionGroupOptionsData = {
        selected: x.isPreselected,
        product_id: x.id,
      };
      if (x.maximumSelections) {
        option.maximumSelections = x.maximumSelections;
      }
      if (x.minimumSelections || x.minimumSelections === 0) {
        option.minimumSelections = x.minimumSelections;
      }
      if (
        x.additionalSelectionPriceAmount &&
        x.additionalSelectionPriceCurrency
      ) {
        option.additionalSelectionPriceAmount = transformPriceInput(
          x.additionalSelectionPriceAmount,
          x.additionalSelectionPriceCurrency
        );
      } else {
        option.additionalSelectionPriceAmount = null;
      }
      if (x.additionalSelectionPriceCurrency) {
        option.additionalSelectionPriceCurrency =
          x.additionalSelectionPriceCurrency;
      }
      return option;
    }, []);

    await submitHandler(toSubmit);
  };

  useHandleFetchError(error);

  React.useEffect(() => {
    if (fields.length === 0) {
      setCurrencyOfFirstProduct(undefined);
    } else {
      if (currencyOfFirstProduct) {
        return;
      }
      setCurrencyOfFirstProduct(
        fields[0].additionalSelectionPriceCurrency as string
      );
    }
    // Not including setCurrencyOfFirstProduct in the dependency array
    // because it causes an infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, setCurrencyOfFirstProduct]);

  if (fetchIsLoading) {
    return <SpinnerBlock />;
  }

  return (
    <FormProvider {...formMethods}>
      <div className="container mt-5">
        <GoBackLinkButton
          to={routes.shopProductAdditionsList(organization)}
          text="Back to all product additions"
        />

        <div className="row">
          <div className="col-12">
            <form className="card" onSubmit={handleSubmit(onSubmit)}>
              <div className="card-header">
                {isEdit ? (
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneRegular card-title"
                  >
                    Edit addition group
                  </StyledText>
                ) : (
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneRegular card-title"
                  >
                    New addition group
                  </StyledText>
                )}
                <div className="card-options">
                  {id !== undefined && (
                    <PrimaryButton
                      type="button"
                      color="white"
                      backgroundColor={colors.errorDark}
                      hoverColor={colors.errorMedium}
                      onClick={async () => {
                        if (
                          !window.confirm(
                            `🛑 Are you sure you want to proceed?.\n${data?.title} will be deleted and no longer be visible in apps and on web.`
                          )
                        ) {
                          return;
                        }

                        await handleDelete(id);
                      }}
                    >
                      {deleteIsLoading ? (
                        <FontAwesomeIcon icon={solid("spinner")} pulse spin />
                      ) : (
                        "Delete"
                      )}
                    </PrimaryButton>
                  )}
                </div>
              </div>
              <div className="card-body">
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2"
                  >
                    Standard Addition group information
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-6"
                  >
                    Below is the standard required information to create an
                    addition group.
                  </StyledText>
                </div>
                <FormRow>
                  <Inputs.InputFormField
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    label="Admin name"
                    name="name"
                    helpText="Only visible for admins"
                  />
                  <Inputs.InputFormField
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    label="Addition group name"
                    name="title"
                    helpText="Visible in app by users"
                  />
                </FormRow>
                <Inputs.InputFormField
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  label="Sort key"
                  name="sortKey"
                  helpText="Enter a number to decide the sorting of the addition group in the list of addition groups within a product. We recommend using intervals of 10. Eg. the first addition group should have sort key “10”. The second sort key “20” etc. All addition groups are sorted ascending."
                />
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2 mt-6"
                  >
                    Addition group configuration
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-6"
                  >
                    Below you configure the addition group type along with
                    minimum and maximum selections.
                  </StyledText>
                </div>
                <Inputs.SelectFormField
                  errors={formMethods.formState.errors}
                  register={formMethods.register}
                  label="Addition group type"
                  name="type"
                  options={typeOptions}
                  clearErrors={formMethods.clearErrors}
                  onChange={(e: any) => {
                    const newVal = e.target.value;
                    setMaximumSelectionsVisible(newVal !== "single");

                    if (newVal === "single") {
                      formMethods.setValue("maximumSelections", null as any);
                      replace(
                        formMethods.getValues().products.map((p) => ({
                          ...p,
                          maximumSelections: 1,
                          additionalSelectionPriceAmount: null,
                        }))
                      );
                    }
                  }}
                  helpText="Choose the type of addition group. Single = the user can only select one addition. Multi = The user can select more than one addition. "
                />
                <FormRow>
                  <Inputs.InputFormField
                    errors={formMethods.formState.errors}
                    register={formMethods.register}
                    label="Minimum Selection"
                    min={0}
                    max={formMethods.watch("type") === "single" ? 1 : undefined}
                    name="minimumSelections"
                    type="number"
                    helpText="If the user is required to choose a minimum selection of product additions, please write the minimum requirement above. E.g. if the user must select 1 addition, write “1”. When choosing a minimum selection, please also choose which additions are “Preselected”. If the user forgets to choose, the preselected addition will be chosen."
                  />
                  {isMaximumSelectionsVisible && (
                    <Inputs.InputFormField
                      errors={formMethods.formState.errors}
                      register={formMethods.register}
                      label="Maximum Selection"
                      min={1}
                      name="maximumSelections"
                      type="number"
                      helpText="If the user is restricted to maximum number of chosen product additions, please write the maximum amount above. E.g. if the user cannot choose more than 4 additions, write “4”."
                    />
                  )}
                </FormRow>
                {currencyOfFirstProduct && isUsingSharedPrice && (
                  <FormRow>
                    <PriceInput
                      currencyString={currencyOfFirstProduct}
                      label="Shared option selection price"
                      formMethods={formMethods}
                      name="sharedOptionSelectionPriceValue"
                      helpertext="You can choose to configure a shared price for the first choices of the user, until they have reached a limit you set. E.g. if you enter 10,00 kr. here, and enter a limit of 4 to the right, the first 4 choices of additions in this additon group will cost 10,00 kr. Choose limit to the right."
                    />
                    <TextInput
                      label="Shared option selection limit"
                      name="sharedOptionSelectionLimit"
                      type="number"
                      helperText="You configure the limit of the shared price across the addition group here. E.g. if you enter 4 here, the first 4 choices of additions in this addition group will cost the amount you enter to the left."
                    />
                  </FormRow>
                )}
                <div>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyHeaderOneSemi mb-2 mt-6"
                  >
                    Product additions
                  </StyledText>
                  <StyledText
                    color={colors.textPrimary}
                    className="bodyTextRegular mb-6"
                  >
                    Below you can add addition products to this addition group.
                    These addition products will be available to choose for the
                    user if the addition group is added to the product they are
                    purchasing. The addition products can be sorted by adding a
                    sort key on the specific product addition under "All
                    products"
                  </StyledText>
                </div>
                <div css={additionsWrapper}>
                  {fields.map((field, index) => (
                    <ProductAddition
                      key={field.id}
                      field={field}
                      index={index}
                      remove={remove}
                    />
                  ))}
                </div>
                <SelectProductsSelector
                  filters={{
                    classification: classification || "production_addition",
                  }}
                  includeClassificationFilter={true}
                  productIdsToLeaveOut={formMethods
                    .watch("products")
                    .map((x: any) => x.id)}
                  buttonText="Add addition products"
                  modalHeader="Add addition products"
                  onSave={(products) => {
                    const currentProducts = formMethods.getValues("products");
                    const productsToAdd: FormProduct[] = products.map((p) => ({
                      id: p.id,
                      name: p.name,
                      title: p.title,
                      isPreselected: false,
                      minimumSelections: 0,
                      maximumSelections: 1,
                      additionalSelectionPriceAmount: "",
                      additionalSelectionPriceCurrency: p.price?.currency,
                    }));
                    replace([...currentProducts, ...productsToAdd]);
                  }}
                />
              </div>
              <div className="card-footer d-flex justify-content-end">
                <ButtonAction
                  disabled={creationisLoading || updateisLoading}
                  type="submit"
                >
                  {creationisLoading || updateisLoading ? (
                    <ActionSpinner />
                  ) : isEdit ? (
                    "Save"
                  ) : (
                    "Create"
                  )}
                </ButtonAction>
              </div>
            </form>
          </div>
        </div>
      </div>
    </FormProvider>
  );
};

const additionGroupSchema = yup.object().shape({
  name: yup.string().required("Enter addition group name to continue"),
  title: yup.string().required("Enter addition group title to continue"),
  type: yup.string().required("Choose addition group type to continue"),
  sharedOptionSelectionPriceValue: yup
    .string()
    .nullable()
    .test("Testing if above zero", "Must be at least 0", (value, ctx) => {
      if (!value) {
        return true;
      }
      return validateAboveOrEqualToZero(
        value,
        ctx.parent.additionalSelectionPriceCurrency
      );
    })
    .test(
      "Testing that both price and limit is entered",
      "Enter shared option selection price to continue",
      (value, ctx) => {
        const sharedOptionSelectionLimit =
          ctx.parent.sharedOptionSelectionLimit;

        if (sharedOptionSelectionLimit && !value) {
          return false;
        }

        return true;
      }
    ),
  sharedOptionSelectionLimit: yup
    .number()
    .transform((value) => (isNaN(value) ? null : value))
    .nullable()
    .min(1, "Value must be at least 1")
    .when("type", {
      is: "multi",
      then: (schema) =>
        schema.test(
          "Testing if above max selection",
          "Limit cannot be above maximum choice in addition group",
          (value, context) => {
            const maximumSelections = context.parent.maximumSelections;
            if (!!value && !!maximumSelections && value > maximumSelections) {
              return false;
            }
            return true;
          }
        ),
      otherwise: (schema) =>
        schema.test(
          "Testing if above min selection",
          "Limit cannot be above 1 when creating a single addition group",
          (value) => {
            if (!value) {
              return true;
            }
            if (value > 1) {
              return false;
            }
            return true;
          }
        ),
    })
    .test(
      "Testing that both price and limit is entered",
      "Enter limit to continue",
      (value, ctx) => {
        const sharedOptionSelectionPriceValue =
          ctx.parent.sharedOptionSelectionPriceValue;

        if (sharedOptionSelectionPriceValue && !value) {
          return false;
        }

        return true;
      }
    ),
  minimumSelections: yup
    .number()
    .typeError("Must be a number")
    .required("Choose a minimum selection amount to continue")
    .min(0, "Value must be at least 0")
    .when("type", {
      is: "single",
      then: yup
        .number()
        .max(1, "Minimum selection cannot exceed 1 when type is single"),
    }),
  maximumSelections: yup.number().when("type", {
    is: "multi",
    then: yup
      .number()
      .typeError("Must be a number")
      .required("Choose a maximum selection to continue")
      .min(1, "Value must be at least 1")
      .test(
        "Testing if max is greater than min",
        "Maximum cannot be less than minimum",
        (value, context) => {
          if (!value) {
            return false;
          }
          const minimumSelections = context.parent.minimumSelections;
          if (minimumSelections > value) {
            return false;
          }
          return true;
        }
      ),
    otherwise: yup.number().nullable(),
  }),
  products: yup.array(
    yup.object({
      isPreselected: yup.boolean().required(),
      maximumSelections: yup
        .number()
        .transform((value) => {
          if (value || value === 0) {
            return value;
          }
          return null;
        })
        .nullable()
        .test(
          "Testing that value is not above group max",
          "Maximum cannot exceed addition group maximum selection",
          (value, context) => {
            // @ts-ignore
            const topLevelFields = context.from[context.from.length - 1].value;
            const groupMaxSelection = topLevelFields.maximumSelections;
            const type = topLevelFields.type;
            if (type === "single") {
              return true;
            }
            if (value) {
              return value <= groupMaxSelection;
            }
            if (value === 0) {
              return true;
            }
            return false;
          }
        )
        .min(1, "Value has to be at least 1"),
      additionalSelectionPriceAmount: yup
        .string()
        .nullable()
        // Not same behavior as other places where we use price as we want to allow null, so cannot use validateAboveZero from PriceInput.tsx file
        .test("Testing if above zero", "Must be at least 0", (value, ctx) => {
          if (!value) {
            return true;
          }
          return validateAboveOrEqualToZero(
            value,
            ctx.parent.additionalSelectionPriceCurrency
          );
        }),
    })
  ),
});

export default ProductAdditionsUpsert;
